import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ImpersonationBroker, asApiError } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { queryKeys } from '../data/queryKeys';
import type { CreateDealResult, DealSubmitPayload } from './types';

export type UseUpdateDealVariables = {
	companyId: string;
	dealId: string;
	payload: DealSubmitPayload;
};

/**
 * PUTs the Update Deal form for an existing deal on a lead. Mirrors the
 * legacy `CustomFormViewModel.save` branch where `dealId` is set
 * (`aida/viewModels/form.ts:555-560`) — the legacy code switches the HTTP
 * verb to PUT against the same `baseApiUrl` (`UpdateDeal/{dealId}`).
 *
 * On success the lead query is invalidated so the next render reflects the
 * updated deal stage / demo perform date; the pacing query is also
 * invalidated since rescheduling can move a demo into / out of today's
 * count.
 */
export function useUpdateDeal() {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation<CreateDealResult, Error, UseUpdateDealVariables>({
		mutationFn: async ({ companyId, dealId, payload }) => {
			const result = await userSession.webServiceHelper.callWebServiceAsync<CreateDealResult>(
				ImpersonationBroker.composeApiUrl({ urlPath: `lead/${companyId}/UpdateDeal/${dealId}` }),
				'PUT',
				{
					...payload.values,
					customProperties: payload.customProperties,
				}
			);
			if (!result.success) {
				throw asApiError(result);
			}
			return result.value ?? {};
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: queryKeys.focusedView.lead(variables.companyId) });
			queryClient.invalidateQueries({ queryKey: queryKeys.focusedView.pacing() });
		},
	});
}
